import {LocalDate, LocalTime} from '@js-joda/core';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableContainer from '@material-ui/core/TableContainer';
import useApiFetch, {apiEndpoint} from '@tracker/shared/src/Auth/Api';
import InlineLoadingIndicator from '@tracker/shared/src/LoadingIndicator/InlineLoadingIndicator';
import {usePreferencesState} from '@tracker/shared/src/Preferences/PreferencesProvider';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {MyComponent, MyProject} from '../components/Tasks/MyProjectsProvider';
import DateRangeSelect, {DateRange, getSingleDayRange, getWeekRange} from '../components/TimeEntry/DateRangeSelect';
import SubmitAllTimeDialog from '../components/TimeEntry/SumitAllTimeDialog';
import TimeEntryFormDialog from '../components/TimeEntry/TimeEntryFormDialog';
import TimeEntryTable from '../components/TimeEntry/TimeEntryTable';
import {useMissingTimeProvider} from "../Providers/MissingTimeProvider";
import {useUnsubmittedContext} from '../Providers/UnsubmittedProvider';
import {mapRawTimeEntry} from "../utils/mapRawTimeEntry";

const useStyles = makeStyles(theme => ({
    dateRangeSelect: {
        margin: theme.spacing(0, 0, 2, 0),
    },
}));

export type Task = {
    id : number;
    name : string;
    favoriteName ?: string;
    component : string;
    componentId : number;
    project : string;
    projectId : number;
    billable : boolean;
};

export type TimeEntry = {
    id : number;
    note : string;
    startDate : LocalDate;
    startTime : LocalTime | null;
    endTime : LocalTime | null;
    minutes : number | null;
    excludeFromBilling : boolean;
    billableMinutesOverride : number | null;
    task : Task | null;
    project ?: MyProject;
    component ?: MyComponent;
    submitted : boolean;
    invoiced : boolean;
    error : string;
    isPMTimeEntry : number | null;
};


const TimeEntries : React.FC = () => {
    const classes = useStyles();
    const apiFetch = useApiFetch();
    const [dateRange, setDateRange] = useState<DateRange | null>(null);
    const [timeEntries, setTimeEntries] = useState<TimeEntry[] | null>(null);
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [submitAllDialogOpen, setSubmitAllDialogOpen] = useState<boolean>(false);
    const [submitIsLoading, setSubmitIsLoading] = useState<boolean>(false);
    const [submitMessage, setSubmitMessage] = useState<string>('');
    const [onlyUnSubmitted, setOnlyUnSubmitted] = useState<boolean>(false);
    const {refreshUnsubmittedCount, increaseUnsubmittedCount, decreaseUnsubmittedCount} = useUnsubmittedContext();
    const {preferences} = usePreferencesState();
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const {addEntry, removeEntry, updateEntry} = useMissingTimeProvider();

    const loadTimeEntries = useCallback(async (dateRange : DateRange) => {
        setTimeEntries(null);

        const url = new URL('/v1/time-entries', apiEndpoint);
        if (onlyUnSubmitted) {
            url.searchParams.set('unSubmitted', '1');
        } else {
            url.searchParams.set('dateStart', dateRange.start.toString());
            url.searchParams.set('dateEnd', dateRange.end.toString());
        }
        try {
            const response = await apiFetch(url.toString());
            const data = await response.json();
            setTimeEntries(data.map(mapRawTimeEntry));
        } catch(e) {
            enqueueSnackbar('An error occurred while fetching time entries.', {variant: 'error'});
        }
    }, [onlyUnSubmitted, apiFetch, enqueueSnackbar]);

    useEffect(() => {
        if (dateRange) {
            return;
        }

        let initialDateRange;

        switch (preferences.timeEntry.initialRange) {
            case 'this-week':
                initialDateRange = getWeekRange(0, preferences.locale.firstDayOfWeek);
                break;

            case 'today':
                initialDateRange = getSingleDayRange(0);
                break;

            case 'yesterday':
                initialDateRange = getSingleDayRange(-1);
                break;
        }

        setDateRange(initialDateRange);
    }, [dateRange, preferences, loadTimeEntries]);

    useEffect(() => {
        if (dateRange) {
            loadTimeEntries(dateRange);
        }
    }, [dateRange, loadTimeEntries, onlyUnSubmitted]);

    const handleInsert = useCallback(async (timeEntry : TimeEntry) => {
        const snackbarKey = enqueueSnackbar('Time entry is being saved…');
        const url = new URL('/v1/time-entries', apiEndpoint);
        try {
            const response = await apiFetch(url.toString(), {
                method: 'POST',
                body: JSON.stringify(timeEntry),
            });

            if (!response.ok) {
                enqueueSnackbar('An error occurred while saving the time entry.', {variant: 'error'});
                return;
            }

            increaseUnsubmittedCount();

            const rawTimeEntry = await response.json();
            const mappedTimeEntry = mapRawTimeEntry(rawTimeEntry);
            addEntry(mappedTimeEntry);

            if (dateRange === null
                || mappedTimeEntry.startDate < dateRange.start
                || mappedTimeEntry.startDate > dateRange.end
            ) {
                setDateRange({start: mappedTimeEntry.startDate, end: mappedTimeEntry.startDate});
                return;
            }

            const newTimeEntries = timeEntries ? [...timeEntries, mappedTimeEntry] : [mappedTimeEntry];
            setTimeEntries(newTimeEntries);
            closeSnackbar(snackbarKey || undefined);
        } catch (e) {
            enqueueSnackbar('An error occurred while saving the time entry.', {variant: 'error'});
        }
    }, [enqueueSnackbar, apiFetch, increaseUnsubmittedCount, addEntry, dateRange, timeEntries, closeSnackbar]);

    const handleUpdate = useCallback(async (timeEntry : TimeEntry) => {
        const snackbarKey = enqueueSnackbar('Time entry is being saved…');
        const url = new URL(`/v1/time-entries/${timeEntry.id}`, apiEndpoint);
        try {
            const response = await apiFetch(url.toString(), {
                method: 'PUT',
                body: JSON.stringify(timeEntry),
            });

            if (!response.ok) {
                enqueueSnackbar('An error occurred while saving the time entry.', {variant: 'error'});
                return;
            }

            if (response.status === 200) {
                const rawTimeEntry = await response.json();
                timeEntry = mapRawTimeEntry(rawTimeEntry);
            }

            updateEntry(timeEntry);

            if (!timeEntries) {
                return;
            }

            const newTimeEntries = [...timeEntries];
            const index = newTimeEntries.findIndex(currentTimeEntry => currentTimeEntry.id === timeEntry.id);

            if (index < 0) {
                return;
            }

            newTimeEntries[index] = timeEntry;
            setTimeEntries(newTimeEntries);
            closeSnackbar(snackbarKey || undefined);
        } catch (e) {
            enqueueSnackbar('An error occurred while saving the time entry.', {variant: 'error'});
        }
    }, [enqueueSnackbar, apiFetch, updateEntry, timeEntries, closeSnackbar]);

    const handleDelete = useCallback(async (timeEntry : TimeEntry) : Promise<boolean> => {
        const url = new URL(`/v1/time-entries/${timeEntry.id}`, apiEndpoint);

        try {
            const response = await apiFetch(url.toString(), {
                method: 'DELETE',
            });

            if (!response.ok) {
                enqueueSnackbar('An error occurred while deleting the time entry.', {variant: 'error'});
                return false;
            }

            removeEntry(timeEntry);
            if (!timeEntries) {
                return true;
            }

            if (!timeEntry.submitted) {
                decreaseUnsubmittedCount();
            }

            const newTimeEntries = timeEntries.filter(currentTimeEntry => currentTimeEntry.id !== timeEntry.id);
            setTimeEntries(newTimeEntries);
            return true;
        } catch (e) {
            enqueueSnackbar('An error occurred while deleting the time entry.', {variant: 'error'});
        }
        return false;
    }, [apiFetch, removeEntry, timeEntries, enqueueSnackbar, decreaseUnsubmittedCount]);

    const handleUnsubmit = useCallback(async (timeEntry : TimeEntry) => {
        const url = new URL(`/v1/time-entries/submit/${timeEntry.id}`, apiEndpoint);

        try {
            const response = await apiFetch(url.toString(), {
                method: 'PATCH',
            });

            if (!response.ok) {
                enqueueSnackbar('An error occurred while unsubmitting the time entry.', {variant: 'error'});
                return;
            }

            const newTimeEntries = timeEntries ? [...timeEntries] : [];
            const index = newTimeEntries.findIndex(currentTimeEntry => currentTimeEntry.id === timeEntry.id);

            if (index < 0) {
                return;
            }

            timeEntry.submitted = false;
            newTimeEntries[index] = timeEntry;
            increaseUnsubmittedCount();
            setTimeEntries(newTimeEntries);
        } catch (e) {
            enqueueSnackbar('An error occurred while unsubmitting the time entry.', {variant: 'error'});
        }
    }, [apiFetch, enqueueSnackbar, increaseUnsubmittedCount, timeEntries]);

    const handleSubmit = useCallback(async (timeEntry : TimeEntry) => {
        setFormDialogOpen(false);
        return handleInsert(timeEntry);
    }, [setFormDialogOpen, handleInsert]);

    const handleSubmitAllTime = useCallback(async () => {
        setSubmitAllDialogOpen(true);
        setSubmitIsLoading(true);
        setSubmitMessage('');
        const url = new URL('/v1/time-entries/submit', apiEndpoint);
        try {
            const response = await apiFetch(url.toString(), {
                method: 'POST',
            });

            if (response.ok) {
                const responseBody = await response.json();

                setSubmitMessage(typeof responseBody.message === 'string'
                    ? responseBody.message
                    : "There was an error getting the result from submitting all time."
                );

                if (responseBody.modified !== undefined && responseBody.modified > 0 && dateRange) {
                    await loadTimeEntries(dateRange);
                }
                setSubmitIsLoading(false);
                refreshUnsubmittedCount();
                return;

            }
            // eslint-disable-next-line no-empty
        } catch (e) {}
        setSubmitIsLoading(false);
        enqueueSnackbar('An error occurred while submitting all time entry records.', {variant: 'error'});
        setSubmitAllDialogOpen(false);
    }, [apiFetch, dateRange, enqueueSnackbar, loadTimeEntries, refreshUnsubmittedCount]);

    const initialValues : Partial<TimeEntry> = useMemo(() => ({
        startDate: preferences.timeEntry.alwaysUseFromDateForNewEntries
            ? dateRange?.start ?? LocalDate.now()
            : (dateRange && dateRange.start.isEqual(dateRange.end)) ? dateRange.start : LocalDate.now()
    }), [dateRange, preferences.timeEntry.alwaysUseFromDateForNewEntries]);

    if (!dateRange) {
        return <InlineLoadingIndicator/>;
    }

    return (
        <React.Fragment>
            <DateRangeSelect
                dateRange={dateRange}
                onChange={setDateRange}
                className={classes.dateRangeSelect}
                onCreateClick={() => setFormDialogOpen(true)}
                handleSubmitAllTime={handleSubmitAllTime}
                onlyUnSubmitted={onlyUnSubmitted}
                setOnlyUnSubmitted={setOnlyUnSubmitted}
            />

            <TimeEntryFormDialog
                timeEntry={initialValues}
                open={formDialogOpen}
                onClose={() => setFormDialogOpen(false)}
                onSubmit={handleSubmit}
            />

            <SubmitAllTimeDialog
                open={submitAllDialogOpen}
                onClose={() => setSubmitAllDialogOpen(false)}
                message={submitMessage}
                isLoading={submitIsLoading}
            />

            <TableContainer component={Paper}>
                {timeEntries === null ? <InlineLoadingIndicator/> : (
                    <TimeEntryTable
                        timeEntries={timeEntries}
                        onDateRangeChange={setDateRange}
                        onInsert={handleInsert}
                        onUpdate={handleUpdate}
                        onDelete={handleDelete}
                        onUnsubmit={handleUnsubmit}
                    />
                )}
            </TableContainer>
        </React.Fragment>
    );
};

export default TimeEntries;
